import type {
  AngleLabelContext,
  LabelContentVariant,
  LabelContext,
  LineLabelContext,
  PointLabelContext,
  PolygonLabelContext,
} from './label.model';

/**
 * Default decimal precision for coordinate display.
 */
const COORD_PRECISION = 2;

/**
 * Default decimal precision for degree display.
 */
const DEGREE_PRECISION = 1;

/**
 * Default decimal precision for length display.
 */
const LENGTH_PRECISION = 2;

/**
 * Format a number with fixed decimal places.
 */
function formatNumber(value: number, precision: number): string {
  return value.toFixed(precision);
}

/**
 * Format coordinates as "(x, y)".
 */
function formatCoordinates(x: number, y: number): string {
  return `(${formatNumber(x, COORD_PRECISION)}, ${formatNumber(y, COORD_PRECISION)})`;
}

/**
 * Format name only.
 */
function formatName(ctx: LabelContext): string {
  return ctx.name;
}

/**
 * Format coordinates only (for points).
 */
function formatCoord(ctx: LabelContext): string {
  if (ctx.type === 'point') {
    const pointCtx = ctx as PointLabelContext;
    return formatCoordinates(pointCtx.x(), pointCtx.y());
  }
  return ctx.name;
}

/**
 * Format name and coordinates: "A (2.00, 3.00)".
 */
function formatNameAndCoord(ctx: LabelContext): string {
  if (ctx.type === 'point') {
    const pointCtx = ctx as PointLabelContext;
    return `${ctx.name} ${formatCoordinates(pointCtx.x(), pointCtx.y())}`;
  }
  return ctx.name;
}

/**
 * Format degrees only (for angles): "45.0°".
 */
function formatDeg(ctx: LabelContext): string {
  if (ctx.type === 'angle') {
    const angleCtx = ctx as AngleLabelContext;
    return `${formatNumber(angleCtx.degrees(), DEGREE_PRECISION)}°`;
  }
  return ctx.name;
}

/**
 * Format name and degrees: "∠ABC = 45.0°".
 */
function formatNameAndDegrees(ctx: LabelContext): string {
  if (ctx.type === 'angle') {
    const angleCtx = ctx as AngleLabelContext;
    return `∠${ctx.name} = ${formatNumber(angleCtx.degrees(), DEGREE_PRECISION)}°`;
  }
  return ctx.name;
}

/**
 * Format length only (for lines): "5.00".
 */
function formatLength(ctx: LabelContext): string {
  if (ctx.type === 'line') {
    const lineCtx = ctx as LineLabelContext;
    return formatNumber(lineCtx.length(), LENGTH_PRECISION);
  }
  return ctx.name;
}

/**
 * Format name and length (for lines): "Δx = 5.00".
 */
function formatNameAndLength(ctx: LabelContext): string {
  if (ctx.type === 'line') {
    const lineCtx = ctx as LineLabelContext;
    return `${ctx.name} = ${formatNumber(lineCtx.length(), LENGTH_PRECISION)}`;
  }
  return ctx.name;
}

/**
 * Registry of label formatters by content variant.
 */
const FORMATTERS: Record<LabelContentVariant, (ctx: LabelContext) => string> = {
  name: formatName,
  coord: formatCoord,
  'name+coord': formatNameAndCoord,
  deg: formatDeg,
  'name+deg': formatNameAndDegrees,
  length: formatLength,
  'name+length': formatNameAndLength,
};

/**
 * Get a formatter function for a content variant.
 *
 * @param variant - The content variant
 * @returns Formatter function
 */
export function getFormatter(variant: LabelContentVariant): (ctx: LabelContext) => string {
  return FORMATTERS[variant];
}

/**
 * Check if text contains template placeholders like {name}, {length}, etc.
 */
function hasTemplatePlaceholders(text: string): boolean {
  return /\{[^}]+\}/.test(text);
}

/**
 * Format text using template placeholders.
 * Replaces {placeholder} with values from the context.
 *
 * Available placeholders by element type:
 * - Points: {name}, {x}, {y}
 * - Lines: {name}, {length}
 * - Angles: {name}, {deg}, {rad}
 * - Polygons: {name}, {area}, {perimeter}
 */
function formatTemplate(template: string, ctx: LabelContext): string {
  let result = template;

  // Common placeholder
  result = result.replace(/\{name\}/g, ctx.name);

  // Type-specific placeholders
  if (ctx.type === 'point') {
    const pointCtx = ctx as PointLabelContext;
    result = result.replace(/\{x\}/g, formatNumber(pointCtx.x(), COORD_PRECISION));
    result = result.replace(/\{y\}/g, formatNumber(pointCtx.y(), COORD_PRECISION));
  } else if (ctx.type === 'line') {
    const lineCtx = ctx as LineLabelContext;
    result = result.replace(/\{length\}/g, formatNumber(lineCtx.length(), LENGTH_PRECISION));
  } else if (ctx.type === 'angle') {
    const angleCtx = ctx as AngleLabelContext;
    result = result.replace(/\{deg\}/g, formatNumber(angleCtx.degrees(), DEGREE_PRECISION));
    result = result.replace(/\{rad\}/g, formatNumber(angleCtx.radians(), 3));
  } else if (ctx.type === 'polygon') {
    const polyCtx = ctx as PolygonLabelContext;
    result = result.replace(/\{area\}/g, formatNumber(polyCtx.area(), COORD_PRECISION));
    result = result.replace(/\{perimeter\}/g, formatNumber(polyCtx.perimeter(), COORD_PRECISION));
  }

  return result;
}

/**
 * Format label text based on configuration.
 * Priority: text (with template support) > formatFn > contentVariant
 *
 * @param config - Label configuration with text, formatFn, or contentVariant
 * @param ctx - Label context for dynamic formatting
 * @returns Formatted label text
 */
export function formatLabelText(
  config: { text?: string; formatFn?: (ctx: LabelContext) => string; contentVariant: LabelContentVariant },
  ctx: LabelContext,
): string {
  // Text has highest priority (supports both static and template)
  if (config.text) {
    if (hasTemplatePlaceholders(config.text)) {
      return formatTemplate(config.text, ctx);
    }
    return config.text;
  }

  // Custom format function
  if (config.formatFn) {
    return config.formatFn(ctx);
  }

  // Use content variant formatter
  return getFormatter(config.contentVariant)(ctx);
}
